import HyperDX from "@hyperdx/browser";

const SERVICE = "solstice";
const OTEL_PROXY = "/api/otel";

// The /api/otel proxy authenticates the request, attaches the server-side
// ClickStack bearer token, and never forwards this header — so the browser key
// is a non-secret sentinel that only suppresses the SDK's empty-key warning.
const PROXY_API_KEY = "proxied";

let initialized = false;
let currentUser: ClickStackUser | undefined;

const resolveEnv = (): string => {
  const vercelEnv = process.env.NEXT_PUBLIC_VERCEL_ENV;
  if (vercelEnv === "production") return "production";
  if (vercelEnv === "preview") return "preview";
  if (vercelEnv === "development") return "development";
  return process.env.NODE_ENV ?? "development";
};

// Must stay byte-identical to the key the deploy uploads source maps under
// (scripts/upload-browser-sourcemaps.mjs) or alerts cannot be symbolicated.
const resolveVersion = (): string | undefined => {
  const commitSha = process.env.NEXT_PUBLIC_COMMIT_SHA;
  return commitSha ? `v${commitSha.toLowerCase()}` : undefined;
};

export const initClickStack = (): void => {
  if (initialized) return;
  if (typeof window === "undefined") return;
  // Guard direct callers too: never start session capture during local dev.
  if (process.env.NODE_ENV === "development") return;

  const env = resolveEnv();
  const version = resolveVersion();

  HyperDX.init({
    apiKey: PROXY_API_KEY,
    service: SERVICE,
    tracesUrl: `${OTEL_PROXY}/traces`,
    logsUrl: `${OTEL_PROXY}/logs`,
    consoleCapture: true,
    advancedNetworkCapture: true,
    disableIntercom: true,
    maskAllInputs: true,
    maskAllText: false,
    // Propagate traceparent to all origins so frontend spans link to backend
    // traces across services. Unlike portal (same-origin only), solstice
    // deliberately accepts the cross-origin CORS-preflight tradeoff for this.
    tracePropagationTargets: [/.*/],
    ignoreUrls: [/\/api\/otel\//],
    otelResourceAttributes: {
      "deployment.environment.name": env,
      ...(version ? { "service.version": version } : {}),
    },
  });

  initialized = true;
};

export interface ClickStackUser {
  id: string;
  email?: string | null;
  name?: string | null;
  organizationId?: number | null;
  organizationName?: string | null;
}

export const setClickStackUser = (user: ClickStackUser): void => {
  if (typeof window === "undefined") return;
  currentUser = user;
  HyperDX.setGlobalAttributes({
    userId: user.id,
    ...(user.email ? { userEmail: user.email } : {}),
    ...(user.name ? { userName: user.name } : {}),
    ...(user.organizationId != null
      ? { "organization.id": String(user.organizationId) }
      : {}),
    ...(user.organizationName
      ? {
          teamName: user.organizationName,
          "organization.name": user.organizationName,
        }
      : {}),
  });
};

export const clearClickStackUser = (): void => {
  if (typeof window === "undefined") return;
  currentUser = undefined;
  HyperDX.setGlobalAttributes({
    userId: "",
    userEmail: "",
    userName: "",
    teamName: "",
    "organization.id": "",
    "organization.name": "",
  });
};

export const captureClickStackError = (
  error: unknown,
  context?: Record<string, unknown>,
): void => {
  if (typeof window === "undefined") return;

  const attributes: Record<string, string> = {};
  if (currentUser) {
    attributes["enduser.id"] = currentUser.id;
    if (currentUser.email) attributes["enduser.email"] = currentUser.email;
    if (currentUser.organizationId != null)
      attributes["organization.id"] = String(currentUser.organizationId);
    if (currentUser.organizationName)
      attributes["organization.name"] = currentUser.organizationName;
  }
  if (context) {
    for (const [key, value] of Object.entries(context)) {
      if (value !== undefined && value !== null) {
        attributes[`context.${key}`] = String(value);
      }
    }
  }

  HyperDX.recordException(
    error instanceof Error ? error : new Error(String(error)),
    attributes,
  );
};
